import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import {
  adminCreateBanner, adminDeleteBanner, adminListBanners,
  adminUpdateBanner, listBanners,
} from "@/lib/api";
import { useApiQuery } from "@/hooks/useApiQuery";
import { useAppSelector } from "@/store";
import { selectIsAdmin } from "@/store/authSlice";
import type { AdminBanner } from "@/types/api";

export function BannerPage() {
  const isAdmin = useAppSelector(selectIsAdmin);
  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">Banner</h1>
      <PublicBannerList />
      {isAdmin && <AdminBannerSection />}
    </div>
  );
}

// ============================================================ public read
function PublicBannerList() {
  const { data, error, loading } = useApiQuery(listBanners, []);
  return (
    <section>
      <h2 className="text-lg font-medium mb-3">Active banners (public)</h2>
      {loading && <p className="text-slate-500">Loading…</p>}
      {error && <ErrBox msg={error} />}
      {data && data.length === 0 && <p className="text-slate-500 text-sm">No active banners.</p>}
      {data && data.length > 0 && (
        <BannerTable rows={data.map((b) => ({ ...b, is_active: true }))} editable={false} onChange={() => {}} />
      )}
    </section>
  );
}

// ============================================================ admin CRUD
function AdminBannerSection() {
  const [rows, setRows] = useState<AdminBanner[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = () => adminListBanners().then(setRows).catch((e) => setError(extractDetail(e, "Load failed.")));
  useEffect(() => { reload(); }, []);

  return (
    <section className="border-t border-slate-200 pt-8 mt-8">
      <h2 className="text-xl font-semibold mb-4">Admin · All banners</h2>
      {error && <ErrBox msg={error} />}
      <CreateBannerForm onCreated={reload} />
      {rows && rows.length > 0 && (
        <div className="mt-6">
          <BannerTable rows={rows} editable onChange={reload} />
        </div>
      )}
    </section>
  );
}

interface NewBanner {
  file_nm: string;
  link: string;
  sort_order: number;
}

function CreateBannerForm({ onCreated }: { onCreated: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const { register, handleSubmit, reset } = useForm<NewBanner>({
    defaultValues: { sort_order: 0 },
  });

  const submit = async (f: NewBanner) => {
    setBusy(true); setMsg(null);
    try {
      await adminCreateBanner({ ...f, sort_order: Number(f.sort_order) });
      reset({ sort_order: 0 });
      onCreated();
    } catch (e: unknown) {
      setMsg(extractDetail(e, "Create failed."));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-medium text-slate-700 mb-3">Create banner</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <input {...register("file_nm", { required: true })} placeholder="file_nm" className={inputCls} />
        <input {...register("link", { required: true })} placeholder="https://..." className={inputCls + " md:col-span-2"} />
        <input {...register("sort_order", { valueAsNumber: true })} type="number" placeholder="sort_order" className={inputCls} />
      </div>
      {msg && <p className="mt-2 text-xs text-red-600">{msg}</p>}
      <div className="mt-3">
        <button type="submit" disabled={busy} className="rounded-md bg-brand text-white px-4 py-2 text-sm font-medium hover:bg-brand-700 disabled:opacity-60">
          {busy ? "Creating…" : "Create"}
        </button>
      </div>
    </form>
  );
}

function BannerTable({ rows, editable, onChange }: {
  rows: AdminBanner[];
  editable: boolean;
  onChange: () => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="px-4 py-2 text-left font-medium">id</th>
            <th className="px-4 py-2 text-left font-medium">file</th>
            <th className="px-4 py-2 text-left font-medium">link</th>
            <th className="px-4 py-2 text-right font-medium">order</th>
            <th className="px-4 py-2 text-left font-medium">active</th>
            {editable && <th className="px-4 py-2 text-right font-medium">actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <BannerRow key={b.id} banner={b} editable={editable} onChange={onChange} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BannerRow({ banner, editable, onChange }: {
  banner: AdminBanner;
  editable: boolean;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const toggleActive = async () => {
    setBusy(true); setMsg(null);
    try { await adminUpdateBanner(banner.id, { is_active: !banner.is_active }); onChange(); }
    catch (e: unknown) { setMsg(extractDetail(e, "Update failed.")); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm(`Delete banner #${banner.id}?`)) return;
    setBusy(true); setMsg(null);
    try { await adminDeleteBanner(banner.id); onChange(); }
    catch (e: unknown) { setMsg(extractDetail(e, "Delete failed.")); }
    finally { setBusy(false); }
  };

  return (
    <tr className="border-t border-slate-100">
      <td className="px-4 py-2 font-mono text-xs">{banner.id}</td>
      <td className="px-4 py-2">{banner.file_nm}</td>
      <td className="px-4 py-2">
        <a href={banner.link} target="_blank" rel="noreferrer" className="text-brand hover:underline">{banner.link}</a>
      </td>
      <td className="px-4 py-2 text-right">{banner.sort_order}</td>
      <td className="px-4 py-2">
        {banner.is_active
          ? <span className="rounded bg-green-50 text-green-700 text-xs px-1.5 py-0.5">active</span>
          : <span className="rounded bg-slate-100 text-slate-500 text-xs px-1.5 py-0.5">inactive</span>}
      </td>
      {editable && (
        <td className="px-4 py-2 text-right text-xs space-x-2">
          {msg && <span className="text-red-600">{msg}</span>}
          <button onClick={toggleActive} disabled={busy} className={btnGhost}>
            {banner.is_active ? "Deactivate" : "Activate"}
          </button>
          <button onClick={remove} disabled={busy} className={`${btnGhost} text-red-600`}>
            Delete
          </button>
        </td>
      )}
    </tr>
  );
}

// ============================================================ ui helpers
const inputCls =
  "block w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-brand focus:ring-2 focus:ring-brand/30 outline-none";
const btnGhost =
  "rounded-md border border-slate-300 px-2.5 py-1 text-xs hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed";

function ErrBox({ msg }: { msg: string }) {
  return <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{msg}</div>;
}

function extractDetail(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fallback;
}
